import {
  Lead,
  LeadEnrichment,
  PersonEnrichment,
  PropertyEnrichment,
  OwnershipData,
  BuildSiteAssessment,
  BehavioralIntent,
  PortfolioEngagement,
  TimberFrameBuyerSignal,
  InferredFinancialCapacity,
  HistoricalPattern,
  BuyerIntelligenceScore,
  TalkingPoint,
  BuyerScoreBand,
  EnhancedDeal,
  AVMData,
  SaleHistoryRecord,
} from '../types';
import { auth } from './firebase';
import { MARKETING_AGENT_ENDPOINT } from './marketingAgentGuide';

const ENRICHMENT_POLL_TIMEOUT_MS = 5 * 60 * 1000;

// ═══════════════════════════════════════════════════════════════════
// Public API
// ═══════════════════════════════════════════════════════════════════

export async function enrichLead(
  lead: Lead,
  enhancedDeals: EnhancedDeal[],
): Promise<LeadEnrichment> {
  if (!lead.id) {
    throw new Error('Lead ID is required for enrichment');
  }

  // Step 1: Queue enrichment via marketing agent API (Mac mini picks up the job)
  await requestMarketingAgentEnrichment(lead.id);

  // Step 2: Wait for the agent to finish and write enrichment data
  const outcome = await waitForMarketingAgentEnrichment(lead.id);
  if (outcome.status === 'failed') {
    throw new Error(outcome.error || 'Marketing agent enrichment failed');
  }

  // Step 3: Re-read server-written enrichment from Firestore
  const serverWrittenData = await readServerEnrichment(lead.id);
  if (!serverWrittenData) {
    throw new Error('Enrichment completed but no data was written to the lead');
  }

  return finalizeLeadEnrichment(lead, enhancedDeals, serverWrittenData);
}

/** Apply client-side scoring and signals on top of agent-written enrichment data. */
export function finalizeLeadEnrichment(
  lead: Lead,
  enhancedDeals: EnhancedDeal[],
  enrichmentFromServer: ServerEnrichmentData,
): LeadEnrichment {
  const behavioral = buildBehavioralIntent(lead);
  const signals = inferBuyerSignals(lead, behavioral, enrichmentFromServer);
  const historicalPattern = computeHistoricalPattern(lead, enhancedDeals);
  const financialCapacity = buildFinancialCapacity(enrichmentFromServer);
  const score = calculateScore(
    signals,
    behavioral,
    financialCapacity,
    enrichmentFromServer?.buildSite,
    historicalPattern,
  );
  const talkingPoints = generateTalkingPoints(
    lead,
    behavioral,
    financialCapacity,
    historicalPattern,
    signals,
    enrichmentFromServer,
  );

  const hasExternalData = enrichmentFromServer?.dataSources?.some(s =>
    ['attom', 'pdl', 'fullcontact', 'web_search', 'hunter', 'trestle'].includes(s),
  );

  return {
    enrichedAt: new Date(),
    lastUpdated: new Date(),
    source: hasExternalData ? 'live' : 'partial',

    person: enrichmentFromServer?.person || undefined,
    property: enrichmentFromServer?.property || undefined,
    ownership: enrichmentFromServer?.ownership || undefined,
    buildSite: enrichmentFromServer?.buildSite || undefined,
    avm: enrichmentFromServer?.avm || undefined,
    saleHistory: enrichmentFromServer?.saleHistory || undefined,

    buyerSignals: signals,
    behavioral: behavioral || undefined,
    historicalPattern: historicalPattern || undefined,
    financialCapacity,

    score,
    talkingPoints,

    dataSources: enrichmentFromServer?.dataSources || [],
    _attomError: enrichmentFromServer?._attomError || undefined,
    _pdlError: enrichmentFromServer?._pdlError || undefined,
    _fullContactError: enrichmentFromServer?._fullContactError || undefined,
  };
}

// ═══════════════════════════════════════════════════════════════════
// Marketing agent enrichment queue
// ═══════════════════════════════════════════════════════════════════

interface EnrichmentRequestOutcome {
  status: 'complete' | 'failed';
  error?: string;
}

async function getStaffIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error('Sign in required to enrich leads');
  }
  return user.getIdToken();
}

export async function requestMarketingAgentEnrichment(leadId: string): Promise<void> {
  const token = await getStaffIdToken();
  const response = await fetch(MARKETING_AGENT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ action: 'requestLeadEnrichment', leadId }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || `Failed to queue enrichment (HTTP ${response.status})`);
  }
}

export function waitForMarketingAgentEnrichment(
  leadId: string,
  timeoutMs = ENRICHMENT_POLL_TIMEOUT_MS,
): Promise<EnrichmentRequestOutcome> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;

    const finish = (outcome: EnrichmentRequestOutcome) => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      clearTimeout(timer);
      resolve(outcome);
    };

    const fail = (err: Error) => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      clearTimeout(timer);
      reject(err);
    };

    const timer = setTimeout(() => {
      fail(new Error('Marketing agent enrichment timed out — ensure the Mac mini agent is running'));
    }, timeoutMs);

    import('firebase/firestore')
      .then(({ doc, onSnapshot }) => import('./firebase').then(({ db }) => {
        const ref = doc(db, 'leadEnrichmentRequests', leadId);
        unsubscribe = onSnapshot(
          ref,
          snap => {
            if (!snap.exists()) return;
            const status = snap.data()?.status;
            if (status === 'complete') {
              finish({ status: 'complete' });
            } else if (status === 'failed') {
              finish({
                status: 'failed',
                error: (snap.data()?.error as string) || 'Enrichment failed',
              });
            }
          },
          err => fail(err instanceof Error ? err : new Error(String(err))),
        );
      }))
      .catch(err => fail(err instanceof Error ? err : new Error(String(err))));
  });
}

interface ServerEnrichmentData {
  person?: PersonEnrichment;
  property?: PropertyEnrichment;
  ownership?: OwnershipData;
  buildSite?: BuildSiteAssessment;
  avm?: AVMData;
  saleHistory?: SaleHistoryRecord[];
  financialCapacity?: InferredFinancialCapacity;
  dataSources?: string[];
  _attomError?: string;
  _pdlError?: string;
  _fullContactError?: string;
}

export async function readServerEnrichment(leadId: string): Promise<ServerEnrichmentData | null> {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('./firebase');

    let docSnap = await getDoc(doc(db, 'leads', leadId));
    if (!docSnap.exists()) {
      docSnap = await getDoc(doc(db, 'testLeads', leadId));
    }
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return data?.enrichment || null;
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════
// Behavioral Intent (from Metrics Lite data already on the lead)
// These are analytics events — not manually entered by the rep
// ═══════════════════════════════════════════════════════════════════

const PORTFOLIO_URL_PATTERNS = [
  /\/portfolio\//i,
  /\/projects?\//i,
  /\/gallery\//i,
  /\/work\//i,
  /\/our-work/i,
];

const FLOOR_PLAN_PATTERNS = [/floor.?plan/i, /plan.?book/i, /plans?\//i];
const PROCESS_PATTERNS = [/\/process/i, /\/how.?it.?works/i, /\/our.?process/i, /\/approach/i];
const PRICING_PATTERNS = [/\/pricing/i, /\/cost/i, /\/investment/i, /\/budget/i];
const TESTIMONIAL_PATTERNS = [/\/testimonial/i, /\/review/i, /\/client.?stories/i];
const BLOG_PATTERNS = [/\/blog\//i, /\/article/i, /\/post\//i, /\/news\//i, /\/journal\//i];
const CONTACT_PATTERNS = [/\/contact/i, /\/get.?in.?touch/i, /\/inquiry/i, /\/request/i];

function extractProjectName(url: string): string | null {
  for (const pattern of PORTFOLIO_URL_PATTERNS) {
    if (pattern.test(url)) {
      const parts = url.replace(/\/$/, '').split('/');
      const slug = parts[parts.length - 1];
      if (slug && slug.length > 2) {
        return slug
          .replace(/[-_]/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase())
          .trim();
      }
    }
  }
  return null;
}

function matchesAny(url: string, patterns: RegExp[]): boolean {
  return patterns.some(p => p.test(url));
}

function extractBlogTitle(url: string): string | null {
  if (!matchesAny(url, BLOG_PATTERNS)) return null;
  const parts = url.replace(/\/$/, '').split('/');
  const slug = parts[parts.length - 1];
  if (slug && slug.length > 3) {
    return slug.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim();
  }
  return null;
}

function toSafeDate(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'object' && 'toDate' in v) { const d = v.toDate(); return isNaN(d.getTime()) ? null : d; }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function buildBehavioralIntent(lead: Lead): BehavioralIntent | null {
  const events = lead.recentEvents;
  const tracking = lead.trackingData;
  const stats = lead.statistics;

  if (!events?.length && !stats?.pageviews) return null;

  const portfolioMap = new Map<string, PortfolioEngagement>();
  let floorPlanPageViews = 0;
  let processPageViews = 0;
  let pricingPageViews = 0;
  let testimonialsPageViews = 0;
  let contactPageViews = 0;
  const blogPostsRead: string[] = [];
  const sessionDates = new Set<string>();

  if (events) {
    for (const event of events) {
      const url = event.url || '';
      const raw: any = event.createdAt;
      const eventDate = raw instanceof Date ? raw
        : (raw && typeof raw === 'object' && 'toDate' in raw) ? raw.toDate()
        : new Date(raw);
      if (isNaN(eventDate.getTime())) continue;
      const dateKey = eventDate.toISOString().split('T')[0];
      sessionDates.add(dateKey);

      const projectName = extractProjectName(url);
      if (projectName) {
        const existing = portfolioMap.get(projectName);
        if (existing) {
          existing.viewCount++;
          existing.totalDwellTimeSeconds += 30;
          if (eventDate > existing.lastViewed) existing.lastViewed = eventDate;
        } else {
          portfolioMap.set(projectName, {
            projectName,
            projectUrl: url,
            viewCount: 1,
            totalDwellTimeSeconds: 30,
            lastViewed: eventDate,
          });
        }
        continue;
      }

      if (matchesAny(url, FLOOR_PLAN_PATTERNS)) floorPlanPageViews++;
      if (matchesAny(url, PROCESS_PATTERNS)) processPageViews++;
      if (matchesAny(url, PRICING_PATTERNS)) pricingPageViews++;
      if (matchesAny(url, TESTIMONIAL_PATTERNS)) testimonialsPageViews++;
      if (matchesAny(url, CONTACT_PATTERNS)) contactPageViews++;

      const blogTitle = extractBlogTitle(url);
      if (blogTitle && !blogPostsRead.includes(blogTitle)) {
        blogPostsRead.push(blogTitle);
      }
    }
  }

  const portfolioProjectsViewed = Array.from(portfolioMap.values())
    .sort((a, b) => b.totalDwellTimeSeconds - a.totalDwellTimeSeconds);

  const uniqueDays = Array.from(sessionDates).sort();
  const daysBetweenFirstAndLastVisit = uniqueDays.length >= 2
    ? Math.round((new Date(uniqueDays[uniqueDays.length - 1]).getTime() - new Date(uniqueDays[0]).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  let returnVisitCount = Math.max(0, uniqueDays.length - 1);
  if (tracking?.firstSeen && tracking?.lastSeen) {
    const firstDate = toSafeDate(tracking.firstSeen);
    const lastDate = toSafeDate(tracking.lastSeen);
    if (firstDate && lastDate) {
      const daySpan = Math.round((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daySpan > daysBetweenFirstAndLastVisit) {
        returnVisitCount = Math.max(returnVisitCount, Math.min(daySpan, 20));
      }
    }
  }

  const totalPagesViewed = stats?.pageviews || events?.length || 0;
  const totalSessions = Math.max(returnVisitCount + 1, 1);
  const galleryViews = portfolioProjectsViewed.reduce((sum, p) => sum + p.viewCount, 0);
  const processViews = processPageViews + pricingPageViews;
  const galleryVsProcessRatio = (galleryViews + processViews) > 0
    ? galleryViews / (galleryViews + processViews)
    : 0.5;

  return {
    portfolioProjectsViewed,
    floorPlanPageViews,
    floorPlanDwellTimeSeconds: floorPlanPageViews * 30,
    processPageViews,
    pricingPageViews,
    testimonialsPageViews,
    blogPostsRead,
    returnVisitCount,
    daysBetweenFirstAndLastVisit,
    totalPagesViewed,
    averageSessionDepthPages: totalPagesViewed / totalSessions,
    contactPageVisitsBeforeSubmission: contactPageViews,
    galleryVsProcessRatio,
  };
}

// ═══════════════════════════════════════════════════════════════════
// Buyer Signals — inferred from EXTERNAL property data + behavioral
// No longer relies on rep-typed notes
// ═══════════════════════════════════════════════════════════════════

function inferBuyerSignals(
  lead: Lead,
  behavioral: BehavioralIntent | null,
  serverData: ServerEnrichmentData | null,
): TimberFrameBuyerSignal[] {
  const signals: TimberFrameBuyerSignal[] = [];

  // ── Signals from ATTOM property data (external, no rep input needed) ──

  if (serverData?.property) {
    const prop = serverData.property;

    if (prop.propertyType === 'vacant_land') {
      signals.push({
        id: 'owns_vacant_land',
        label: 'Owns Vacant Land',
        category: 'readiness',
        confidence: 'confirmed',
        description: `ATTOM confirms the address is vacant land${prop.lotSizeAcres ? ` (${prop.lotSizeAcres.toFixed(1)} acres)` : ''}. Primary timber frame readiness signal.`,
        scoreImpact: 15,
      });
    }

    if (prop.hasExistingStructure && prop.yearBuilt && prop.yearBuilt < 1970) {
      signals.push({
        id: 'teardown_candidate',
        label: 'Possible Teardown',
        category: 'readiness',
        confidence: 'inferred',
        description: `Property has a ${prop.yearBuilt} structure (${prop.buildingSqft?.toLocaleString() || '?'} sqft). May be planning a rebuild.`,
        scoreImpact: 5,
      });
    }

    if (prop.lotSizeAcres && prop.lotSizeAcres >= 2) {
      signals.push({
        id: 'large_lot',
        label: 'Large Lot',
        category: 'readiness',
        confidence: 'confirmed',
        description: `${prop.lotSizeAcres.toFixed(1)} acres — ample room for a timber frame home with setbacks.`,
        scoreImpact: 3,
      });
    }
  }

  if (serverData?.ownership) {
    const own = serverData.ownership;

    if (own.ownershipStatus === 'absentee') {
      signals.push({
        id: 'absentee_owner',
        label: 'Absentee Owner',
        category: 'life_chapter',
        confidence: 'confirmed',
        description: `ATTOM shows absentee ownership — likely a second home or investment property build.`,
        scoreImpact: 5,
      });
    }

    if (own.estimatedEquity && own.estimatedEquity >= 200000) {
      signals.push({
        id: 'high_equity',
        label: 'Significant Home Equity',
        category: 'readiness',
        confidence: 'confirmed',
        description: `Estimated ${formatCurrency(own.estimatedEquity)} in property equity — strong indicator of financial capacity for a timber frame build.`,
        scoreImpact: 8,
      });
    }

    if (own.yearsOfOwnership && own.yearsOfOwnership >= 15) {
      signals.push({
        id: 'long_term_owner',
        label: 'Long-Term Owner',
        category: 'life_chapter',
        confidence: 'confirmed',
        description: `Owned property for ${own.yearsOfOwnership} years. May be planning their "forever home" or next chapter.`,
        scoreImpact: 3,
      });
    }
  }

  if (serverData?.avm?.value) {
    if (serverData.avm.value >= 500000) {
      signals.push({
        id: 'high_value_property',
        label: 'High-Value Property',
        category: 'readiness',
        confidence: 'confirmed',
        description: `AVM estimates property at ${formatCurrency(serverData.avm.value)}. Well within timber frame buyer range.`,
        scoreImpact: 8,
      });
    } else if (serverData.avm.value >= 200000) {
      signals.push({
        id: 'moderate_value_property',
        label: 'Property Valued',
        category: 'readiness',
        confidence: 'confirmed',
        description: `AVM estimates property at ${formatCurrency(serverData.avm.value)}.`,
        scoreImpact: 3,
      });
    }
  }

  if (serverData?.property && !serverData?.avm?.value) {
    signals.push({
      id: 'property_data_found',
      label: 'Property Data Found',
      category: 'readiness',
      confidence: 'confirmed',
      description: 'Property records found via ATTOM but no AVM valuation available for this address.',
      scoreImpact: 2,
    });
  }

  if (serverData?.financialCapacity?.capacityLevel === 'strong') {
    signals.push({
      id: 'strong_financial',
      label: 'Strong Financial Profile',
      category: 'readiness',
      confidence: 'confirmed',
      description: 'Property data indicates strong financial capacity for a custom timber frame build.',
      scoreImpact: 5,
    });
  }

  // ── Signals from behavioral data (Metrics Lite — automatic, no rep input) ──

  if (behavioral) {
    if (behavioral.blogPostsRead.length > 0) {
      signals.push({
        id: 'content_reader',
        label: 'Content Reader',
        category: 'aesthetic',
        confidence: 'confirmed',
        description: `Read ${behavioral.blogPostsRead.length} blog post${behavioral.blogPostsRead.length > 1 ? 's' : ''}: ${behavioral.blogPostsRead.slice(0, 2).join(', ')}. Deep content engagement signals genuine interest in the craft.`,
        scoreImpact: 3,
      });
    }

    if (behavioral.portfolioProjectsViewed.length >= 3) {
      signals.push({
        id: 'portfolio_deep_dive',
        label: 'Portfolio Deep-Dive',
        category: 'aesthetic',
        confidence: 'confirmed',
        description: `Studied ${behavioral.portfolioProjectsViewed.length} portfolio projects. Serious evaluation of your work.`,
        scoreImpact: 5,
      });
    }

    if (behavioral.returnVisitCount >= 3 && behavioral.daysBetweenFirstAndLastVisit >= 7) {
      signals.push({
        id: 'sustained_interest',
        label: 'Sustained Interest',
        category: 'readiness',
        confidence: 'confirmed',
        description: `${behavioral.returnVisitCount + 1} visits over ${behavioral.daysBetweenFirstAndLastVisit} days. This is not impulse — they've been considering you seriously.`,
        scoreImpact: 5,
      });
    }

    if (behavioral.floorPlanPageViews >= 2) {
      signals.push({
        id: 'floor_plan_interest',
        label: 'Floor Plan Interest',
        category: 'readiness',
        confidence: 'confirmed',
        description: `Viewed floor plans ${behavioral.floorPlanPageViews} times. Actively comparing designs.`,
        scoreImpact: 5,
      });
    }

    if (behavioral.pricingPageViews > 0 && behavioral.processPageViews > 0) {
      signals.push({
        id: 'evaluation_mode',
        label: 'Evaluation Mode',
        category: 'readiness',
        confidence: 'confirmed',
        description: 'Viewed both pricing and process pages. Past inspiration, into serious evaluation.',
        scoreImpact: 5,
      });
    }
  }

  // ── Signals from Person data (PDL/FullContact — by email/phone, no address needed) ──

  if (serverData?.person) {
    const person = serverData.person;

    if (person.inferredSalary) {
      const numbers = person.inferredSalary.match(/[\d,]+/g)?.map(n => parseInt(n.replace(/,/g, ''), 10)) || [];
      const maxSalary = Math.max(...numbers, 0);

      if (maxSalary >= 200000) {
        signals.push({
          id: 'high_income',
          label: 'High Income',
          category: 'readiness',
          confidence: 'confirmed',
          description: `PDL inferred salary: ${person.inferredSalary}. Well within timber frame budget range.`,
          scoreImpact: 8,
        });
      } else if (maxSalary >= 100000) {
        signals.push({
          id: 'moderate_income',
          label: 'Moderate Income',
          category: 'readiness',
          confidence: 'confirmed',
          description: `PDL inferred salary: ${person.inferredSalary}. May need financing for a timber frame project.`,
          scoreImpact: 3,
        });
      }
    }

    if (person.jobTitle) {
      const seniorTitles = /\b(ceo|cfo|cto|coo|president|vp|vice\s*president|partner|director|owner|founder|principal|managing|chief)\b/i;
      if (seniorTitles.test(person.jobTitle)) {
        signals.push({
          id: 'senior_professional',
          label: 'Senior Professional',
          category: 'life_chapter',
          confidence: 'confirmed',
          description: `${person.jobTitle}${person.jobCompany ? ` at ${person.jobCompany}` : ''}. Senior professionals are a core timber frame buyer demographic.`,
          scoreImpact: 5,
        });
      }
    }

    if (person.linkedinUrl) {
      signals.push({
        id: 'verified_identity',
        label: 'Verified Identity',
        category: 'competitive',
        confidence: 'confirmed',
        description: `LinkedIn profile found${person.likelihood ? ` (match confidence: ${person.likelihood}/10)` : ''}. Identity confirmed.`,
        scoreImpact: 3,
      });
    }

    if (person.fullName || person.jobTitle) {
      signals.push({
        id: 'person_enriched',
        label: 'Person Identified',
        category: 'competitive',
        confidence: 'confirmed',
        description: `Person data discovered: ${person.fullName || 'name unknown'}${person.jobTitle ? `, ${person.jobTitle}` : ''}${person.jobCompany ? ` at ${person.jobCompany}` : ''}.`,
        scoreImpact: 3,
      });
    }

    if (person.homeAddress?.street) {
      signals.push({
        id: 'home_address_found',
        label: 'Home Address Found',
        category: 'readiness',
        confidence: 'confirmed',
        description: `Home address discovered: ${[person.homeAddress.street, person.homeAddress.city, person.homeAddress.state].filter(Boolean).join(', ')}. Used for property data lookup.`,
        scoreImpact: 5,
      });
    }
  }

  // ── Lead source signal (from CRM field — not notes) ──

  if (lead.source === 'referral') {
    signals.push({
      id: 'referral_source',
      label: 'Referral',
      category: 'competitive',
      confidence: 'confirmed',
      description: 'Lead came through a referral. Referral leads typically close at the highest rate.',
      scoreImpact: 5,
    });
  }

  // ── Flood zone signal (from FEMA API — external) ──

  if (serverData?.buildSite?.floodZone === 'none') {
    signals.push({
      id: 'clear_flood_zone',
      label: 'No Flood Risk',
      category: 'readiness',
      confidence: 'confirmed',
      description: 'FEMA confirms no flood zone designation. Clear for construction.',
      scoreImpact: 2,
    });
  } else if (serverData?.buildSite?.floodZone === 'high' || serverData?.buildSite?.floodZone === 'coastal_high') {
    signals.push({
      id: 'flood_risk',
      label: 'Flood Zone',
      category: 'readiness',
      confidence: 'confirmed',
      description: `FEMA shows ${serverData.buildSite.floodZone === 'coastal_high' ? 'coastal high-risk' : 'high-risk'} flood zone. May require elevated foundation or flood insurance.`,
      scoreImpact: -3,
    });
  }

  return signals;
}

// ═══════════════════════════════════════════════════════════════════
// Financial Capacity — built from ATTOM property data, not notes
// ═══════════════════════════════════════════════════════════════════

function buildFinancialCapacity(serverData: ServerEnrichmentData | null): InferredFinancialCapacity {
  // Start with server-computed capacity if available
  if (serverData?.financialCapacity) {
    return serverData.financialCapacity;
  }

  const marketValue = serverData?.avm?.value || serverData?.property?.estimatedMarketValue || serverData?.property?.assessedValue || 0;
  const flags: string[] = [];

  // Use PDL salary data when no property data exists
  let capacityFromSalary: 'strong' | 'moderate' | 'unknown' | 'limited' = 'unknown';
  if (serverData?.person?.inferredSalary) {
    const numbers = serverData.person.inferredSalary.match(/[\d,]+/g)?.map(n => parseInt(n.replace(/,/g, ''), 10)) || [];
    const maxSalary = Math.max(...numbers, 0);
    capacityFromSalary = maxSalary >= 200000 ? 'strong' : maxSalary >= 100000 ? 'moderate' : 'unknown';
    flags.push(`PDL inferred salary: ${serverData.person.inferredSalary}`);
  }

  const capacityFromProperty = marketValue >= 500000 ? 'strong' : marketValue >= 250000 ? 'moderate' : 'unknown';

  // Use the stronger of the two signals
  const levels = { 'strong': 3, 'moderate': 2, 'unknown': 1, 'limited': 0 };
  const capacityLevel = levels[capacityFromProperty] >= levels[capacityFromSalary] ? capacityFromProperty : capacityFromSalary;

  if (marketValue === 0 && !serverData?.person?.inferredSalary) {
    flags.push('No property or income data available — financial capacity unknown');
  }

  return {
    primaryHomeValue: marketValue || undefined,
    combinedAssets: marketValue,
    hasMortgageOfRecord: false,
    capacityLevel,
    cashBuyerIndicators: false,
    constructionLoanMentioned: false,
    flags,
  };
}

// ═══════════════════════════════════════════════════════════════════
// Historical Patterns (from real deal data — internal analytics)
// ═══════════════════════════════════════════════════════════════════

function computeHistoricalPattern(lead: Lead, deals: EnhancedDeal[]): HistoricalPattern | null {
  if (!deals || deals.length < 3) return null;

  const closedDeals = deals.filter(d => d.outcome === 'won' || d.outcome === 'lost');
  if (closedDeals.length < 3) return null;

  const wonDeals = closedDeals.filter(d => d.outcome === 'won');
  const lostDeals = closedDeals.filter(d => d.outcome === 'lost');

  const overallCloseRate = closedDeals.length > 0 ? wonDeals.length / closedDeals.length : 0;

  const sameSourceDeals = closedDeals.filter(d => d.source === lead.source);
  const sameSourceWon = sameSourceDeals.filter(d => d.outcome === 'won');
  const sourceCloseRate = sameSourceDeals.length >= 3
    ? sameSourceWon.length / sameSourceDeals.length
    : overallCloseRate;

  const avgDaysToClose = wonDeals.length > 0
    ? Math.round(wonDeals.reduce((sum, d) => {
        const created = toSafeDate(d.createdAt);
        const closed = toSafeDate(d.closedAt || d.updatedAt);
        if (!created || !closed) return sum;
        return sum + (closed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
      }, 0) / wonDeals.length)
    : 0;

  const avgDealValue = wonDeals.length > 0
    ? Math.round(wonDeals.reduce((sum, d) => sum + (d.value || 0), 0) / wonDeals.length)
    : 0;

  const lostReasonCounts = new Map<string, number>();
  for (const d of lostDeals) {
    const reason = d.lostReason?.trim();
    if (reason) {
      lostReasonCounts.set(reason, (lostReasonCounts.get(reason) || 0) + 1);
    }
  }
  const commonLostReasons = Array.from(lostReasonCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([reason]) => reason);

  const sourceQuality: HistoricalPattern['sourceQualityRating'] =
    sourceCloseRate >= 0.4 ? 'high' : sourceCloseRate >= 0.2 ? 'medium' : 'low';

  return {
    similarLeadCloseRate: sameSourceDeals.length >= 3 ? sourceCloseRate : overallCloseRate,
    sampleSize: sameSourceDeals.length >= 3 ? sameSourceDeals.length : closedDeals.length,
    averageDaysToClose: avgDaysToClose,
    averageDealValue: avgDealValue,
    commonLostReasons,
    sourceQualityRating: sourceQuality,
  };
}

// ═══════════════════════════════════════════════════════════════════
// Score Calculator
// ═══════════════════════════════════════════════════════════════════

function calculateScore(
  signals: TimberFrameBuyerSignal[],
  behavioral: BehavioralIntent | null,
  financial: InferredFinancialCapacity,
  buildSite: BuildSiteAssessment | null | undefined,
  historical: HistoricalPattern | null,
): BuyerIntelligenceScore {
  let buildSiteQualification = 0;
  let projectReadiness = 0;
  let lifeChapterFinancial = 0;
  let behavioralEngagement = 0;
  let penalties = 0;
  const penaltyReasons: string[] = [];

  for (const signal of signals) {
    const impact = signal.scoreImpact;
    if (impact < 0) {
      penalties += impact;
      penaltyReasons.push(signal.description);
      continue;
    }

    switch (signal.category) {
      case 'readiness':
        if (['owns_vacant_land', 'large_lot', 'clear_flood_zone', 'high_equity'].includes(signal.id)) {
          buildSiteQualification += impact;
        } else {
          projectReadiness += impact;
        }
        break;
      case 'life_chapter':
        lifeChapterFinancial += impact;
        break;
      case 'aesthetic':
        behavioralEngagement += impact;
        break;
      case 'competitive':
        projectReadiness += impact;
        break;
    }
  }

  if (financial.capacityLevel === 'strong') lifeChapterFinancial += 5;

  buildSiteQualification = Math.min(buildSiteQualification, 25);
  projectReadiness = Math.min(projectReadiness, 30);
  lifeChapterFinancial = Math.min(lifeChapterFinancial, 25);
  behavioralEngagement = Math.min(behavioralEngagement, 20);

  if (financial.capacityLevel === 'limited') {
    penalties -= 10;
    penaltyReasons.push('Inferred financial capacity below typical timber frame range');
  }
  if (behavioral && behavioral.totalPagesViewed <= 1 && behavioral.returnVisitCount === 0) {
    penalties -= 5;
    penaltyReasons.push('Minimal website engagement');
  }

  const total = Math.max(0, buildSiteQualification + projectReadiness + lifeChapterFinancial + behavioralEngagement + penalties);

  let band: BuyerScoreBand = 'cold';
  if (total >= 80) band = 'blazing';
  else if (total >= 60) band = 'hot';
  else if (total >= 40) band = 'warm';
  else if (total >= 20) band = 'cool';

  return {
    total,
    band,
    breakdown: { buildSiteQualification, projectReadiness, lifeChapterFinancial, behavioralEngagement },
    penalties,
    penaltyReasons,
  };
}

// ═══════════════════════════════════════════════════════════════════
// Talking Points Generator — uses external data, not notes
// ═══════════════════════════════════════════════════════════════════

function generateTalkingPoints(
  lead: Lead,
  behavioral: BehavioralIntent | null,
  financial: InferredFinancialCapacity,
  historical: HistoricalPattern | null,
  signals: TimberFrameBuyerSignal[],
  serverData: ServerEnrichmentData | null,
): TalkingPoint[] {
  const points: TalkingPoint[] = [];

  // Person-based talking points (from PDL/FullContact — by email/phone)
  if (serverData?.person) {
    const person = serverData.person;

    if (person.jobTitle && person.jobCompany) {
      points.push({
        text: `They work as ${person.jobTitle} at ${person.jobCompany}${person.jobCompanyIndustry ? ` (${person.jobCompanyIndustry})` : ''}. Tailor the conversation to their professional context.`,
        category: 'action',
        priority: 2,
      });
    }

    if (person.inferredSalary) {
      points.push({
        text: `Estimated income: ${person.inferredSalary}. ${
          person.inferredSalary.includes('250') || person.inferredSalary.includes('500') || person.inferredSalary.includes('1,000')
            ? 'Strong financial fit for a custom timber frame build.'
            : 'Financing may be part of the conversation.'
        }`,
        category: 'financial',
        priority: 2,
      });
    }

    if (person.linkedinUrl) {
      points.push({
        text: `LinkedIn profile available — review before the call to personalize the conversation.`,
        category: 'action',
        priority: 3,
      });
    }

    if (person.homeAddress?.street && !serverData?.property) {
      points.push({
        text: `Home address found: ${person.homeAddress.street}, ${person.homeAddress.city}, ${person.homeAddress.state}. ATTOM couldn't match this address — may want to verify.`,
        category: 'property',
        priority: 3,
      });
    }
  }

  // Property-based talking points (from ATTOM — zero rep effort)
  if (serverData?.property) {
    const prop = serverData.property;
    if (prop.propertyType === 'vacant_land') {
      points.push({
        text: `Their address is vacant land${prop.lotSizeAcres ? ` (${prop.lotSizeAcres.toFixed(1)} acres)` : ''}. They likely already own a build site — ask about their vision for it.`,
        category: 'property',
        priority: 1,
      });
    } else if (prop.hasExistingStructure) {
      points.push({
        text: `Property has an existing ${prop.yearBuilt || ''} structure (${prop.buildingSqft?.toLocaleString() || '?'} sqft). Ask if they're building on a different site or planning a replacement.`,
        category: 'property',
        priority: 2,
      });
    }
  }

  if (serverData?.ownership?.estimatedEquity && serverData.ownership.estimatedEquity >= 200000) {
    points.push({
      text: `Property records show significant equity (${formatCurrency(serverData.ownership.estimatedEquity)}). They likely have the financial capacity for a custom timber frame build.`,
      category: 'financial',
      priority: 2,
    });
  }

  if (serverData?.avm?.value) {
    const conf = serverData.avm.confidence;
    points.push({
      text: `AVM estimates their property at ${formatCurrency(serverData.avm.value)}${conf ? ` (confidence: ${conf}/100)` : ''}. ${serverData.avm.value >= 500000 ? 'Well within timber frame buyer range.' : 'May need to explore financing options.'}`,
      category: 'financial',
      priority: 3,
    });
  }

  if (serverData?.buildSite?.floodZone === 'high' || serverData?.buildSite?.floodZone === 'coastal_high') {
    points.push({
      text: 'FEMA shows the site is in a flood zone. Discuss elevated foundation options and how timber frames handle this well.',
      category: 'property',
      priority: 2,
    });
  }

  // Behavioral talking points (from Metrics Lite — automatic tracking)
  if (behavioral && behavioral.portfolioProjectsViewed.length > 0) {
    const topProjects = behavioral.portfolioProjectsViewed.slice(0, 2);
    const projectStr = topProjects.map(p => p.projectName).join(' and ');
    points.push({
      text: `They spent the most time studying ${projectStr}. Lead with those styles in conversation.`,
      category: 'engagement',
      priority: 2,
    });
  }

  if (behavioral && behavioral.floorPlanPageViews >= 2) {
    points.push({
      text: `They visited floor plans ${behavioral.floorPlanPageViews} times${behavioral.daysBetweenFirstAndLastVisit > 0 ? ` over ${behavioral.daysBetweenFirstAndLastVisit} days` : ''}. They're comparing options and may be ready for a design conversation.`,
      category: 'engagement',
      priority: 2,
    });
  }

  if (behavioral && behavioral.blogPostsRead.length > 0) {
    points.push({
      text: `They read your content on ${behavioral.blogPostsRead.slice(0, 2).join(' and ')}. This is someone who cares about the craft — lead with quality and process, not price.`,
      category: 'engagement',
      priority: 3,
    });
  }

  if (behavioral && behavioral.returnVisitCount >= 3) {
    points.push({
      text: `They visited the site ${behavioral.returnVisitCount + 1} times over ${behavioral.daysBetweenFirstAndLastVisit} days. They did their homework — this is a considered inquiry.`,
      category: 'engagement',
      priority: 3,
    });
  }

  // Historical talking points (from CRM deal analytics — internal data)
  if (historical && historical.sampleSize >= 3) {
    const rateStr = `${Math.round(historical.similarLeadCloseRate * 100)}%`;
    const valueStr = historical.averageDealValue > 0 ? ` with an avg value of ${formatCurrency(historical.averageDealValue)}` : '';
    const daysStr = historical.averageDaysToClose > 0 ? ` and ${historical.averageDaysToClose}-day cycle` : '';
    points.push({
      text: `Leads from this source close at ${rateStr} historically (n=${historical.sampleSize})${valueStr}${daysStr}.`,
      category: 'historical',
      priority: 3,
    });
  }

  if (historical && historical.commonLostReasons.length > 0) {
    points.push({
      text: `Common lost reasons for similar leads: ${historical.commonLostReasons.slice(0, 2).join(', ')}. Address these proactively.`,
      category: 'historical',
      priority: 4,
    });
  }

  // Financial guidance
  if (financial.capacityLevel === 'unknown' && !serverData?.property) {
    points.push({
      text: 'No property data available yet. Explore budget range and financing plans early in the conversation.',
      category: 'financial',
      priority: 2,
    });
  }

  // ATTOM not configured message
  if (serverData?._attomError && !serverData.dataSources?.includes('attom')) {
    points.push({
      text: 'Property data unavailable — ATTOM API key not configured. Add it in Settings to unlock property, ownership, and valuation intelligence.',
      category: 'action',
      priority: 5,
    });
  }

  // Lead source
  if (lead.source) {
    const referral = signals.find(s => s.id === 'referral_source');
    if (!referral) {
      const sourceLabel = lead.source.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      points.push({
        text: `Lead source: ${sourceLabel}. ${historical?.sourceQualityRating === 'high' ? 'This is a high-quality source for your business.' : historical?.sourceQualityRating === 'low' ? 'This source has historically had lower conversion. Qualify more carefully.' : ''}`.trim(),
        category: 'action',
        priority: 4,
      });
    }
  }

  return points.sort((a, b) => a.priority - b.priority);
}

// ═══════════════════════════════════════════════════════════════════
// Utility exports
// ═══════════════════════════════════════════════════════════════════

export function getScoreBandColor(band: BuyerScoreBand): string {
  switch (band) {
    case 'blazing': return '#dc2626';
    case 'hot': return '#ea580c';
    case 'warm': return '#ca8a04';
    case 'cool': return '#2563eb';
    case 'cold': return '#6b7280';
  }
}

export function getScoreBandLabel(band: BuyerScoreBand): string {
  switch (band) {
    case 'blazing': return 'Blazing';
    case 'hot': return 'Hot';
    case 'warm': return 'Warm';
    case 'cool': return 'Cool';
    case 'cold': return 'Cold';
  }
}

export function getCapacityColor(level: string): string {
  switch (level) {
    case 'strong': return '#16a34a';
    case 'moderate': return '#ca8a04';
    case 'limited': return '#dc2626';
    default: return '#6b7280';
  }
}

export function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}k`;
  return `$${value}`;
}
